import { CHART_STYLE, skin } from './skins';
import { tradingView } from './tradingview';

/**
 * Trademate's widgets (N75) — live's `src/pages/Trademate/*` on main. The
 * Autochartist ones keep their signed URLs, each wearing the shell's skin from
 * `skins.js` (its `css` parameter) and, where it draws charts, the themed
 * `chart_style`.
 *
 * The other four Screener tabs are Darqube's (widget.darqube.com) — one key
 * token each, issued for this app. Darqube serves a widget only to the domain
 * it is registered for in its dashboard, read from the referrer (see `Frame`),
 * and draws it in the colours set there too: the frame can't restyle it.
 */

/**
 * Autochartist signs each product on its own now — one token and one expiry
 * per widget, rather than the single pair these all used to share. Volatility,
 * Market News and Market Report happen to be issued the same token; that is
 * Autochartist's doing, not a copy/paste here, so each is still named for
 * itself and can be reissued alone.
 *
 * Replaced on 2026-10-12, before the previous set (`expire=1795989600`,
 * 30 Nov 2026) ran out. These run to 29–30 Apr 2028, after which every frame
 * below stops loading until Autochartist issues new ones.
 *
 * Only the signature moves when they are reissued: `locale`, `layout`,
 * `account_type`, `css` and `chart_style` are this app's own and are not part
 * of what Autochartist signs.
 */
const sign = (token, expire) =>
  `broker_id=826&token=${token}&expire=${expire}&user=oz@alphacapitalgroup.uk`;

const AC = {
  opportunities: sign('b5b87765b874e5ec0021f4f257c3df3d', 1840665563),
  volatility: sign('372145dff38017ddcca4f76187c9eaf1', 1840665564),
  calendar: sign('4ffe36f77c138a50bd3c9b3fe029409a', 1840665600),
  news: sign('372145dff38017ddcca4f76187c9eaf1', 1840665564),
  report: sign('372145dff38017ddcca4f76187c9eaf1', 1840665564),
  sentiment: sign('318a24f66d67b1423fa7002f8b299356', 1840665565),
};

const DARQUBE = 'https://widget.darqube.com';

export const SCREENER = [
  {
    value: 'opportunities',
    label: 'Trading Opportunity',
    src: `https://component.autochartist.com/to/?${AC.opportunities}&locale=en_GB&layout=horizontal&account_type=LIVE&trade_now=y&style=ds&chart_style=${CHART_STYLE}&css=${skin('to')}#/results`,
  },
  {
    value: 'volatility',
    label: 'Volatility Analysis',
    // This one reads its settings from the hash route, not the query.
    src: `https://component.autochartist.com/va/#/results?account_type=LIVE&${AC.volatility}&locale=en-GB&style=ds&chart_style=${CHART_STYLE}&css=${skin('va')}`,
  },
  {
    value: 'screener',
    label: 'Screener',
    src: `${DARQUBE}/screener-widget?token=6ac611e7a5e2e369e985f89b`,
  },
  {
    value: 'overview',
    label: 'Market Overview',
    src: `${DARQUBE}/market-overview?token=6ac6183568f5a92c3fa84e6e`,
  },
  {
    value: 'forex',
    label: 'Forex Matrix',
    src: `${DARQUBE}/forex-widget?token=6ac61276a5e2e369e985f90d`,
  },
  {
    value: 'tape',
    label: 'Ticker Tape',
    src: `${DARQUBE}/ticker-tape?token=6ac617a3b82dd4df45c11186`,
  },
];

/**
 * TradingView's advanced chart with live's settings. `backgroundColor` is
 * `--bg-surface`'s raw value, since the frame can't read our tokens. Live's
 * `calendar: true` is left out: TradingView retired it, and its own embed
 * script drops it from the settings it hands the frame.
 */
export const CHART_SRC = tradingView('advanced-chart', {
  autosize: true,
  symbol: 'FX:EURUSD',
  timezone: 'Etc/UTC',
  theme: 'dark',
  range: '5D',
  withdateranges: true,
  hide_side_toolbar: false,
  allow_symbol_change: true,
  details: true,
  hotlist: true,
  backgroundColor: '#0e0e0e',
  support_host: 'https://www.tradingview.com',
});

export const CALENDAR_SRC = `https://eia.autochartist.com/calendar/?${AC.calendar}&showall=true&nextdays=3&locale=en&style=ds&css=${skin('calendar')}#!/calendar`;

export const NEWS_SRC = `https://component.autochartist.com/news/stock-news?${AC.news}&account_type=LIVE&locale=en&css=${skin('news')}`;

/**
 * Autochartist's News Sentiment, in the dark of its two themes — the shell has
 * no light mode to serve the other one to. This product names the language
 * `language`, not `locale` like the rest.
 */
export const SENTIMENT_SRC = `https://news-sentiment.autochartist.com/news-sentiment?theme=dark&${AC.sentiment}&account_type=LIVE&language=en&style=ds`;

export const REPORT = [
  {
    value: 'report',
    label: 'Market Report Email',
    src: `https://reports.autochartist.com/mr/?${AC.report}&locale=en&rid=835&demo=0&css=${skin('report')}&chart_style=${CHART_STYLE}#/report/latest`,
  },
  {
    value: 'signup',
    label: 'Sign-up form',
    /*
     * Spelled out rather than built from `sign()`, because this product
     * differs in both halves of the signature: it names the broker `bid`, and
     * its `user` is the literal "email" — the form collects an address, so
     * putting the account owner's in would be answering its own question.
     * Its token is its own too, not a typo for the Market Report's above.
     */
    src: `https://reports.autochartist.com/signup/market-reports?bid=826&user=email&demo=0&locale=en&expire=1840665559&token=d5ab452394b655b9d9a5dcd74bbae4cf&css=${skin('signup')}`,
  },
];

export const TABS = [
  { value: 'screener', label: 'Market Screener' },
  { value: 'calendar', label: 'Economic Calendar' },
  { value: 'news', label: 'Market News' },
  { value: 'journal', label: 'Trade Journal' },
  { value: 'report', label: 'Market Report' },
  { value: 'chart', label: 'TradingView Chart' },
];

export const NEWS_TABS = [
  { value: 'feed', label: 'News Feed' },
  { value: 'sentiment', label: 'News Sentiment' },
  { value: 'twitter', label: 'Twitter Feed' },
];
